import { useEffect, useRef, useState } from 'react';

import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { Mercator } from '@visx/geo';
import { ParentSize } from '@visx/responsive';
import clsx from 'clsx';
import * as topojson from 'topojson-client';

import { Location } from '../../../../../universal-dashboard-api/src/Data-Processor/Entities/Interfaces/Summary';
import { DeviceGroupStyles, FeatureShape } from '../../../types';
import {
  configs,
  COUNTRY_NAMES,
  PADDING_Y,
  REGION_INTERVAL_DURATION,
  RegionId,
} from './const';

type GlobalMapBaseProps = {
  width: number;
  height: number;
  locations: Location[];
  deviceStyles?: DeviceGroupStyles;
};

type FeatureCollection = {
  type: 'FeatureCollection';
  features: FeatureShape[];
};

function getMapRegionIndexFromQueryParams(): number {
  const region = new URLSearchParams(window.location.search).get('region');

  if (!region) {
    return -1;
  }
  return configs.findIndex((config) => config.id === (region as RegionId));
}

function MapGlobalBase({
  width,
  height,
  locations,
  deviceStyles,
}: GlobalMapBaseProps) {
  const intervalId = useRef<number>();

  // If the region is specified in the query params, fix the map to that region,
  // otherwise iterate through regions
  const specifiedRegionIndex = getMapRegionIndexFromQueryParams();
  const shouldIterateThroughRegions = specifiedRegionIndex < 0;
  const [index, setIndex] = useState(
    shouldIterateThroughRegions ? 0 : specifiedRegionIndex,
  );
  const [world, setWorld] = useState<FeatureCollection | undefined>(undefined);

  useEffect(() => {
    async function loadTopo() {
      const res = await fetch(`${process.env.PUBLIC_URL}/world-topo.json`);
      const worldTopology = await res.json();

      // @ts-expect-error
      const world = topojson.feature(
        worldTopology,
        worldTopology.objects.countries,
      ) as FeatureCollection;
      setWorld(world);
    }

    // The world topojson file is pretty hefty, so we load it async rather than as part of the bundle
    loadTopo();
  }, []);

  useEffect(() => {
    if (!shouldIterateThroughRegions) {
      return;
    }
    intervalId.current = window.setInterval(() => {
      // Only switch views when tab is considered active
      if (!document.hidden) {
        setIndex((index + 1) % configs.length);
      }
    }, REGION_INTERVAL_DURATION);

    return () => {
      window.clearInterval(intervalId.current);
    };
  }, [index, intervalId, shouldIterateThroughRegions]);

  if (!deviceStyles || !world) {
    return null;
  }

  const config = configs[index];

  const isGlobalView = index === 0;

  const configFeatures = isGlobalView
    ? world.features.filter(
        (feature) => feature.properties.name !== COUNTRY_NAMES.Antarctica,
      )
    : world.features.filter((feature) =>
        config.highlightedCountryNames?.includes(feature.properties.name),
      );

  const featureCollection = {
    type: 'FeatureCollection',
    features: configFeatures,
  };

  return (
    <svg height={height} width={width}>
      <Mercator<FeatureShape>
        data={world.features}
        fitExtent={[
          [
            [0, PADDING_Y],
            [width, height - PADDING_Y],
          ],
          featureCollection as any,
        ]}
      >
        {(mercator) => {
          return (
            <>
              <g>
                {mercator.features.map((feature, i) => (
                  <path
                    key={`map-feature-${i}`}
                    className={clsx(
                      'fill-purple-90 stroke-purple-70',
                      config.highlightedCountryNames &&
                        !config.highlightedCountryNames?.includes(
                          feature.feature.properties.name,
                        ) &&
                        'opacity-30',
                    )}
                    d={feature.path || ''}
                  />
                ))}
              </g>
              {!isGlobalView && (
                <g>
                  {locations.map((location, index) => {
                    const coord = mercator.projection([
                      location.longitude,
                      location.latitude,
                    ]);

                    if (!coord) {
                      return null;
                    }

                    const iconScale = height * 0.00004;
                    const iconOffsetX = 200 * iconScale;
                    const iconOffsetY = 500 * iconScale;

                    return (
                      <g
                        key={`${location.latitude}-${index}`}
                        transform={`translate(${coord[0] - iconOffsetX}, ${
                          coord[1] - iconOffsetY
                        }) scale(${iconScale})`}
                      >
                        <path
                          className={`${deviceStyles[location.type].fill} ${
                            deviceStyles[location.type].stroke
                          }`}
                          d={faLocationDot.icon[4] as string}
                        />
                      </g>
                    );
                  })}
                </g>
              )}
            </>
          );
        }}
      </Mercator>
    </svg>
  );
}

export function MapGlobal(
  props: Pick<GlobalMapBaseProps, 'locations' | 'deviceStyles'>,
) {
  return (
    <ParentSize className="hidden lg:block">
      {({ width, height }) => (
        <MapGlobalBase width={width} height={height} {...props} />
      )}
    </ParentSize>
  );
}
